import React, { useMemo } from "react";

function clampRot(r) {
  const x = ((r % 360) + 360) % 360;
  const snaps = [0, 90, 180, 270];
  let best = snaps[0];
  let bestD = Infinity;
  for (const s of snaps) {
    const d = Math.abs(s - x);
    if (d < bestD) {
      bestD = d;
      best = s;
    }
  }
  return best;
}

function rotatePoint(x, y, deg, cx, cy) {
  const a = (deg * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const dx = x - cx;
  const dy = y - cy;
  return {
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  };
}

export default function MiragioCaptchaSolutionSheet() {
  // ✅ rotations gagnantes (internal only)
  const winning = useMemo(() => ({ 1: 90, 2: 0, 3: 270, 4: 180, 5: 90 }), []);

  const segmentsByLayer = useMemo(
    () => ({
      1: [
        [70, 60, 220, 60],
        [220, 60, 220, 220],
        [70, 220, 220, 220],
        [70, 60, 70, 220],
      ],
      2: [
        [260, 70, 450, 70],
        [260, 140, 450, 140],
        [260, 210, 450, 210],
      ],
      3: [
        [120, 95, 200, 175],
        [200, 175, 280, 95],
        [280, 95, 360, 175],
        [360, 175, 440, 95],
      ],
      4: [
        [95, 140, 235, 140],
        [235, 140, 235, 250],
        [235, 250, 425, 250],
      ],
      5: [
        [110, 40, 110, 120],
        [410, 40, 410, 120],
        [110, 120, 410, 120],
        [260, 120, 260, 240],
      ],
    }),
    []
  );

  const layerColorClass = (id) => {
    const map = {
      1: "text-cyan-300",
      2: "text-fuchsia-300",
      3: "text-amber-300",
      4: "text-emerald-300",
      5: "text-indigo-300",
    };
    return map[id] || "text-slate-200";
  };

  // viewBox auto pour ne jamais couper (rotations + marge + ombres)
  const viewBox = useMemo(() => {
    const cx = 260;
    const cy = 140;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    for (let id = 1; id <= 5; id++) {
      const rot = clampRot(winning[id] ?? 0);
      const segs = segmentsByLayer[id] || [];

      for (const s of segs) {
        const p1 = rotatePoint(s[0], s[1], rot, cx, cy);
        const p2 = rotatePoint(s[2], s[3], rot, cx, cy);

        minX = Math.min(minX, p1.x, p2.x);
        minY = Math.min(minY, p1.y, p2.y);
        maxX = Math.max(maxX, p1.x, p2.x);
        maxY = Math.max(maxY, p1.y, p2.y);
      }
    }

    const pad = 70;
    const x = Math.floor(minX - pad);
    const y = Math.floor(minY - pad);
    const w = Math.ceil(maxX - minX + pad * 2);
    const h = Math.ceil(maxY - minY + pad * 2);

    return `${x} ${y} ${w} ${h}`;
  }, [segmentsByLayer, winning]);

  const renderLayer = (id) => {
    const segs = segmentsByLayer[id] || [];
    const centerX = 260;
    const centerY = 140;

    return (
      <g
        key={id}
        transform={`rotate(${clampRot(winning[id])} ${centerX} ${centerY})`}
        opacity={0.95}
        style={{
          filter: "drop-shadow(0 0 6px rgba(0,0,0,0.18))",
          mixBlendMode: "multiply",
        }}
      >
        {segs.map((s, i) => (
          <line
            key={`${id}-${i}`}
            x1={s[0]}
            y1={s[1]}
            x2={s[2]}
            y2={s[3]}
            stroke="currentColor"
            strokeWidth="5"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </g>
    );
  };

  return (
    <div className="w-full">
      <div className="relative mx-auto w-full max-w-[980px] rounded-2xl border border-black/10 bg-[#f6f0e6] shadow-[0_25px_80px_rgba(0,0,0,0.55)] overflow-hidden">
        {/* grain léger */}
        <div
          className="absolute inset-0 opacity-[0.20] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 25% 18%, rgba(0,0,0,0.06), transparent 48%)," +
              "radial-gradient(circle at 78% 24%, rgba(0,0,0,0.05), transparent 56%)," +
              "linear-gradient(135deg, rgba(0,0,0,0.04), transparent 35%, rgba(0,0,0,0.03) 70%, transparent)",
          }}
        />

        <div className="relative p-4 sm:p-6">
          {/* ✅ petites infos “contextuelles inutiles” */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="text-[11px] uppercase tracking-[0.22em] text-black/55">
              Feuille de référence
            </div>
            <div className="text-[11px] font-mono text-black/45">ref: VIS-COND-01</div>
          </div>

          <div className="rounded-xl border border-black/10 bg-white/55 p-3">
            {/* ✅ aspect stable + viewBox auto */}
            <div className="relative w-full aspect-[16/9] sm:aspect-[2/1]">
              <svg
                viewBox={viewBox}
                preserveAspectRatio="xMidYMid meet"
                className="absolute inset-0 w-full h-full"
                style={{ overflow: "visible" }}
              >
                {/* cadre discret */}
                <rect
                  x="18"
                  y="18"
                  width="484"
                  height="244"
                  rx="18"
                  fill="none"
                  stroke="rgba(0,0,0,0.08)"
                />

                <g>
                  {[1, 2, 3, 4, 5].map((id) => (
                    <g key={id} className={layerColorClass(id)}>
                      {renderLayer(id)}
                    </g>
                  ))}
                </g>
              </svg>
            </div>
          </div>

          {/* ✅ une micro-ligne “ambiance” mais non-guidante */}
          <div className="mt-3 text-[11px] text-black/40 italic">
            (papier de contrôle — archive interne)
          </div>
        </div>
      </div>
    </div>
  );
}